import { Component, OnInit } from "@angular/core";
import { FormArray, FormBuilder, FormGroup } from "@angular/forms";
import { DateAdapter, MatSnackBar } from "@angular/material";
import { BranchService } from "@app/admin/branch/branch.service";
import { OrgUnit } from "@app/admin/organization-structure/organization-structure.model";
import { OrganizationStructureService } from "@app/admin/organization-structure/organization-struture.service";
import { ReferenceCode } from "@app/admin/reference-code/reference-code.model";
import { ReferenceCodeService } from "@app/admin/reference-code/reference-code.service";
import { NameValueDto } from "@app/loan-od-accounts/name-value-dto";
import { BranchStructureService } from "../branch-struture.service";
import { ActivatedRoute } from "@angular/router";


@Component({
  selector: 'eng-org-flat',
  templateUrl: './branch-structure.template.html',
  styleUrls: ['../branch-structure.component.scss']
})

export class OrgFlatComponent implements OnInit {

  orgFlatSearchForm: FormGroup;
  userBranches: NameValueDto[];
  orgStructure: any;
  orgChains: ReferenceCode[];
  allLevels: OrgUnit[] = [];
  mode: 'view' | 'edit' = 'view';

  selectedOrgFlatToEdit: any;
  dropdownValues: any;
  form: FormGroup;
  showList: boolean;
  chainCode: string;
  // this branchCode value will come from branch component when launch button is clicked
  incomingBranchCode: string;
  listOfFeatures: string[];

  constructor(private formBuilder: FormBuilder,
    private dateAdapter: DateAdapter<Date>,
    private branchService: BranchService,
    private referenceCodeService: ReferenceCodeService,
    private organizationStructureService: OrganizationStructureService,
    private branchStructureService: BranchStructureService,
    private _snackbar: MatSnackBar,
    private route: ActivatedRoute) {
    this.route.queryParams.subscribe(params => {
      this.incomingBranchCode = params['code'];
    });
  }


  ngOnInit() {
    this.orgFlatSearchForm = this.formBuilder.group({
      branch: ""
    });
    this.getUserBranches();
    this.getReferenceCodes();
    this.referenceCodeService.getFeatureSet().subscribe(response => {
      this.listOfFeatures = response.body.map(feature => feature.toLocaleLowerCase());
    })
  }

  getReferenceCodes() {
    this.referenceCodeService.getRefCodesForClassifier('org_chain').subscribe((response: any) => {
      this.orgChains = response;
      this.getLevel();
    });
  }

  getUserBranches() {
    this.branchService.getAllBranches().subscribe((response: any) => {
      this.userBranches = response;

      if (this.incomingBranchCode) {
        this.orgFlatSearchForm.controls.branch.setValue(this.incomingBranchCode);
      } else {
        this.orgFlatSearchForm.controls.branch.setValue(this.userBranches[0].code);
      }
      this.getFlatOrgStructure();
    });
  }

  getLevel() {
    this.organizationStructureService.getLevels('').subscribe((response: any) => {
      let levels = response.body;

      // Group by chain and we are removing base level, for base levelNum will be null
      let grouped: any = {};
      levels.filter(level => level.levelNum !== null).forEach((lvl: any) => {
        if (!grouped[lvl.chain]) {
          grouped[lvl.chain] = [];
        }
        grouped[lvl.chain].push(lvl);
      });

      // Sort each group by levelNum ascending order, if we need the structure from bottom to top we can change this to decending order
      Object.keys(grouped).forEach(chain => {
        grouped[chain].sort((a, b) => a.levelNum - b.levelNum);
      });

      // Convert to array for template
      this.allLevels = Object.keys(grouped).filter(chain => {
        let chainDef = this.orgChains.find(c => c.code === chain);
        if (!chainDef || chainDef.field1 == null) {
          return true; // keep if no field1
        }
        return String(chainDef.field1).trim() !== "-1"; // skip only -1
      }).map(chain => ({
        chain,
        levels: grouped[chain]
      })) // Sort based on field1 from orgChains
        .sort((a, b) => {
          // Find the orgChain entry for 'a' and 'b' using chain code
          let aChain = this.orgChains.find(c => c.code === a.chain);
          let bChain = this.orgChains.find(c => c.code === b.chain);

          // Get the field1 values (if present), otherwise use a large number so they go last
          let aVal = aChain.field1 ? +aChain.field1.trim() : Number.MAX_SAFE_INTEGER;
          let bVal = bChain.field1 ? +bChain.field1.trim() : Number.MAX_SAFE_INTEGER;

          // Sort by numeric field1 value (smaller comes first)
          return aVal - bVal;
        });
    })
  }

  getChainLevels(chainCode: string) {
    return this.allLevels.find(c => c.chain === chainCode).levels || [];
  }

  hasChainData(chainCode: string): boolean {
    if (!this.orgStructure || !this.orgStructure.chain) {
      return false;
    }
    return this.orgStructure.chain.some(c => c.code === chainCode);
  }

  getFlatOrgStructure() {
    this.mode = 'view';
    this.showList = false; // temporarily hide UI
    this.orgStructure = null;
    this.organizationStructureService.fetchOrgFlatMasters(this.orgFlatSearchForm.value.branch, this.orgFlatSearchForm.value.chain).subscribe(response => {
      this.orgStructure = response.body ? { ...response.body } : null;
      this.showList = true; // show only when fresh data is set
    })
  }

  getOrgUnit(chainCode: string, levelNum: number) {
    if (!this.orgStructure.chain) {
      return null;
    }
    let chain = this.orgStructure.chain.find(c => c.code === chainCode);
    if (!chain) return null;

    return chain.flatOrgUnits.find(u => u.levelNum === levelNum) || null;
  }

  getLevelName(chainCode: string, levelNum: number): string {
    let level = this.getChainLevels(chainCode).find(l => l.levelNum === levelNum);
    return level ? `${level.name} (Level ${level.levelNum})` : 'N/A';
  }

  editChain(chainCode: string) {
    this.mode = 'edit';
    this.chainCode = chainCode;

    // Find the selected chain in the current orgStructure (if it exists)
    let existingChain = this.orgStructure.chain ? this.orgStructure.chain.find(c => c.code === chainCode) : null;

    // Initialize selectedOrgFlatToEdit safely
    this.selectedOrgFlatToEdit = {
      branchCode: this.orgStructure.branchCode,
      chain: [
        {
          code: chainCode,
          uid: existingChain ? existingChain.uid : 0,
          flatOrgUnits: existingChain ? existingChain.flatOrgUnits.slice() : []
        }
      ]
    };

    // Get the definition of this chain from allLevels
    let chainDefinition = this.allLevels.find(c => c.chain === chainCode);

    // Initialize form with levels array
    this.form = this.formBuilder.group({
      levels: this.formBuilder.array([])
    });

    let levelsFormArray = this.form.get('levels') as FormArray;

    // Populate form array with levels (excluding base level 0)
    chainDefinition.levels.filter(level => level.levelNum !== 0).forEach(levelDef => {
      // Match existing org unit if editing
      let matchedUnit = existingChain
        ? existingChain.flatOrgUnits.find(u => u.levelNum === levelDef.levelNum) : null;

      levelsFormArray.push(this.formBuilder.group({
        levelNum: [levelDef.levelNum],
        selectedUid: [matchedUnit ? matchedUnit.orgUnitUid : null]
      }));
    });

    // Fetch dropdown values for this chain
    this.organizationStructureService.getOrgStructureList(chainCode).subscribe(response => {
      this.dropdownValues = response.body;
    });
  }

  getOptionsForLevel(levelNum: number): any[] {
    if (!this.dropdownValues || !this.dropdownValues.levelUnits) return [];
    let level = this.dropdownValues.levelUnits.find(l => l.levelNum === levelNum);
    return level ? level.orgStructure : [];
  }

  saveOrgFlat() {
    let levelsFormArray = this.form.get('levels') as FormArray;

    // Rebuild flatOrgUnits directly from form values
    this.selectedOrgFlatToEdit.chain[0].flatOrgUnits = levelsFormArray.value.map(v => ({
      levelNum: v.levelNum,
      orgUnitUid: v.selectedUid
    }));

    this.branchStructureService
      .saveOrEditOrgFlat(this.selectedOrgFlatToEdit, this.selectedOrgFlatToEdit.chain[0].uid, this.selectedOrgFlatToEdit.branchCode, this.selectedOrgFlatToEdit.chain[0].code)
      .subscribe(response => {
        if (response.body) {
          this._snackbar.open("Branch structure updated successfully", "Close", {
            duration: 4000,
          });
          this.closeEditCard();
          this.getFlatOrgStructure();
        }
      });
  }

  closeEditCard() {
    this.mode = 'view';
  }
}